import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { gradeIdFromAnnualCellKey, parseAnnualCellKey } from '../utils/annualTimetable';
import {
  auditLearningBodySnippet,
  clearPresentationRefFromLearningContentRow,
  readProgramLearningContentFields,
  writeProgramLearningContentFields,
} from '../utils/programLearningContentFields';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';
import {
  isPersistableUuid,
  resolvePresentationRefIdFromLearningContent,
} from '../utils/programContentSlideOwner';
import { supabase } from '../supabaseClient';
import { upsertProgramFixedContentRows } from './programFixedContentService';
import {
  deleteProgramContentSlidePagesForOwner,
  fetchProgramContentSlidePages,
  hasProgramContentSlideDraftForOwner,
  invalidateProgramContentSlidePresenceCache,
  learningTextStorageWithoutPresentationRef,
  programContentSlidePresenceKey,
} from './programContentSlideService';
import { upsertProgramProgressCells } from './programProgressService';
import { upsertProgramScopedContentRows } from './programScopedContentService';

export type EmptyPresentationBindingSourceKind = 'scoped_row' | 'fixed_row' | 'program_cell';

export type EmptyPresentationBindingEntry = {
  selectionKey: string;
  sourceKind: EmptyPresentationBindingSourceKind;
  sourceId: string;
  presentationRefId: string;
  contextLabel: string;
  learningSnippet: string;
};

function learningSnippet(row: ProgramScopedContentRow | ProgramFixedContentRow | ProgramProgressCell): string {
  return auditLearningBodySnippet(row);
}

export function emptyPresentationBindingSelectionKey(
  sourceKind: EmptyPresentationBindingSourceKind,
  sourceId: string
): string {
  return `empty-ref:${sourceKind}:${sourceId.trim()}`;
}

export function parseEmptyPresentationBindingSelectionKey(
  key: string
): { sourceKind: EmptyPresentationBindingSourceKind; sourceId: string } | null {
  if (!key.startsWith('empty-ref:')) return null;
  const parts = key.split(':');
  if (parts.length !== 3) return null;
  const sourceKind = parts[1] as EmptyPresentationBindingSourceKind;
  const sourceId = parts[2]?.trim() ?? '';
  if (!sourceId || !isPersistableUuid(sourceId)) return null;
  if (sourceKind !== 'scoped_row' && sourceKind !== 'fixed_row' && sourceKind !== 'program_cell') return null;
  return { sourceKind, sourceId };
}

async function ownerHasAnySlidePages(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<boolean> {
  const pages = await fetchProgramContentSlidePages(workspaceId, owner, { forceRefresh: true });
  return pages.length > 0;
}

async function rowHasAnySlideOrDraft(params: {
  workspaceId: string;
  learningText: string | null | undefined;
  owners: ProgramContentSlideOwnerRef[];
}): Promise<boolean> {
  const sid = params.workspaceId.trim();
  if (!sid) return false;
  for (const owner of params.owners) {
    if (!owner.id?.trim()) continue;
    if (hasProgramContentSlideDraftForOwner(sid, owner)) return true;
    if (await ownerHasAnySlidePages(sid, owner)) return true;
  }
  return false;
}

function ownersForScopedRow(row: ProgramScopedContentRow): ProgramContentSlideOwnerRef[] {
  const owners: ProgramContentSlideOwnerRef[] = [];
  const ref = resolvePresentationRefIdFromLearningContent(row);
  const id = (row.id ?? '').trim();
  if (ref) owners.push({ kind: 'presentation_ref', id: ref });
  if (id && isPersistableUuid(id)) owners.push({ kind: 'scoped_row', id });
  return owners;
}

function ownersForFixedRow(row: ProgramFixedContentRow): ProgramContentSlideOwnerRef[] {
  const owners: ProgramContentSlideOwnerRef[] = [];
  const ref = resolvePresentationRefIdFromLearningContent(row);
  const id = (row.id ?? '').trim();
  if (ref) owners.push({ kind: 'presentation_ref', id: ref });
  if (id && isPersistableUuid(id)) owners.push({ kind: 'fixed_row', id });
  return owners;
}

function ownersForProgramCell(cell: ProgramProgressCell): ProgramContentSlideOwnerRef[] {
  const owners: ProgramContentSlideOwnerRef[] = [];
  const ref = resolvePresentationRefIdFromLearningContent(cell);
  const id = (cell.id ?? '').trim();
  if (ref) owners.push({ kind: 'presentation_ref', id: ref });
  if (id && isPersistableUuid(id)) owners.push({ kind: 'program_cell', id });
  return owners;
}

export async function findEmptyPresentationBindingsForScope(params: {
  workspaceId: string;
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  programProgressCells: ProgramProgressCell[];
}): Promise<EmptyPresentationBindingEntry[]> {
  const sid = params.workspaceId.trim();
  if (!sid) return [];
  const out: EmptyPresentationBindingEntry[] = [];

  for (const row of params.scopedRows) {
    const sourceId = (row.id ?? '').trim();
    const presentationRefId = resolvePresentationRefIdFromLearningContent(row);
    if (!sourceId || !isPersistableUuid(sourceId) || !presentationRefId) continue;
    const owners = ownersForScopedRow(row);
    if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: row.learning_text, owners })) continue;
    out.push({
      selectionKey: emptyPresentationBindingSelectionKey('scoped_row', sourceId),
      sourceKind: 'scoped_row',
      sourceId,
      presentationRefId,
      contextLabel: `${row.scope_type}|${row.scope_key} #${row.order_index}`,
      learningSnippet: learningSnippet(row),
    });
  }

  for (const row of params.fixedRows) {
    const sourceId = (row.id ?? '').trim();
    const presentationRefId = resolvePresentationRefIdFromLearningContent(row);
    if (!sourceId || !isPersistableUuid(sourceId) || !presentationRefId) continue;
    const owners = ownersForFixedRow(row);
    if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: row.learning_text, owners })) continue;
    out.push({
      selectionKey: emptyPresentationBindingSelectionKey('fixed_row', sourceId),
      sourceKind: 'fixed_row',
      sourceId,
      presentationRefId,
      contextLabel: `order #${row.order_index}${row.source_annual_cell_key ? ` · ${row.source_annual_cell_key}` : ''}`,
      learningSnippet: learningSnippet(row),
    });
  }

  for (const cell of params.programProgressCells) {
    const sourceId = (cell.id ?? '').trim();
    const presentationRefId = resolvePresentationRefIdFromLearningContent(cell);
    if (!sourceId || !isPersistableUuid(sourceId) || !presentationRefId) continue;
    const owners = ownersForProgramCell(cell);
    if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: cell.learning_text, owners })) continue;
    out.push({
      selectionKey: emptyPresentationBindingSelectionKey('program_cell', sourceId),
      sourceKind: 'program_cell',
      sourceId,
      presentationRefId,
      contextLabel: `${cell.monday_iso} ${cell.weekday_key} #${cell.slot_index}`,
      learningSnippet: learningSnippet(cell),
    });
  }

  return out.sort((a, b) => a.selectionKey.localeCompare(b.selectionKey, 'en'));
}

async function persistLearningTextWithoutPresentationRef(params: {
  workspaceId: string;
  sourceKind: EmptyPresentationBindingSourceKind;
  scopedRow?: ProgramScopedContentRow;
  fixedRow?: ProgramFixedContentRow;
  programCell?: ProgramProgressCell;
  nextLearningText?: string | null;
}): Promise<void> {
  if (!params.workspaceId.trim()) return;

  if (params.sourceKind === 'scoped_row' && params.scopedRow) {
    await upsertProgramScopedContentRows([clearPresentationRefFromLearningContentRow(params.scopedRow)]);
    return;
  }

  if (params.sourceKind === 'fixed_row' && params.fixedRow) {
    await upsertProgramFixedContentRows([clearPresentationRefFromLearningContentRow(params.fixedRow)]);
    return;
  }

  if (params.sourceKind === 'program_cell' && params.programCell) {
    await upsertProgramProgressCells([clearPresentationRefFromLearningContentRow(params.programCell)]);
  }
}

async function loadScopedRowById(
  workspaceId: string,
  sourceId: string
): Promise<ProgramScopedContentRow | null> {
  const { data, error } = await supabase
    .from('program_scoped_content_rows')
    .select('*')
    .eq('workspace_id', workspaceId.trim())
    .eq('id', sourceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramScopedContentRow | null) ?? null;
}

async function loadFixedRowById(
  workspaceId: string,
  sourceId: string
): Promise<ProgramFixedContentRow | null> {
  const { data, error } = await supabase
    .from('program_fixed_content_rows')
    .select('*')
    .eq('workspace_id', workspaceId.trim())
    .eq('id', sourceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramFixedContentRow | null) ?? null;
}

async function loadProgramCellById(
  workspaceId: string,
  sourceId: string
): Promise<ProgramProgressCell | null> {
  const { data, error } = await supabase
    .from('program_progress_cells')
    .select('*')
    .eq('workspace_id', workspaceId.trim())
    .eq('id', sourceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramProgressCell | null) ?? null;
}

export async function deleteEmptyPresentationBindingSelections(params: {
  workspaceId: string;
  selectionKeys: string[];
  scopedRows?: ProgramScopedContentRow[];
  fixedRows?: ProgramFixedContentRow[];
  programProgressCells?: ProgramProgressCell[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  if (!sid || !params.selectionKeys.length) return;
  const scopedById = new Map((params.scopedRows ?? []).map((row) => [row.id, row] as const));
  const fixedById = new Map((params.fixedRows ?? []).map((row) => [row.id, row] as const));
  const cellById = new Map((params.programProgressCells ?? []).map((cell) => [cell.id, cell] as const));
  const seen = new Set<string>();

  for (const key of params.selectionKeys) {
    const parsed = parseEmptyPresentationBindingSelectionKey(key);
    if (!parsed || seen.has(key)) continue;
    seen.add(key);
    if (parsed.sourceKind === 'scoped_row') {
      const row = scopedById.get(parsed.sourceId) ?? (await loadScopedRowById(sid, parsed.sourceId));
      if (!row) continue;
      const owners = ownersForScopedRow(row);
      if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: row.learning_text, owners })) continue;
      await persistLearningTextWithoutPresentationRef({
        workspaceId: sid,
        sourceKind: 'scoped_row',
        scopedRow: row,
      });
      continue;
    }
    if (parsed.sourceKind === 'fixed_row') {
      const row = fixedById.get(parsed.sourceId) ?? (await loadFixedRowById(sid, parsed.sourceId));
      if (!row) continue;
      const owners = ownersForFixedRow(row);
      if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: row.learning_text, owners })) continue;
      await persistLearningTextWithoutPresentationRef({
        workspaceId: sid,
        sourceKind: 'fixed_row',
        fixedRow: row,
      });
      continue;
    }
    const cell = cellById.get(parsed.sourceId) ?? (await loadProgramCellById(sid, parsed.sourceId));
    if (!cell) continue;
    const owners = ownersForProgramCell(cell);
    if (await rowHasAnySlideOrDraft({ workspaceId: sid, learningText: cell.learning_text, owners })) continue;
    await persistLearningTextWithoutPresentationRef({
      workspaceId: sid,
      sourceKind: 'program_cell',
      programCell: cell,
    });
  }

  invalidateProgramContentSlidePresenceCache(sid);
}

export async function pruneEmptyPresentationBindingsForScope(params: {
  workspaceId: string;
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  programProgressCells: ProgramProgressCell[];
}): Promise<EmptyPresentationBindingEntry[]> {
  const bindings = await findEmptyPresentationBindingsForScope(params);
  if (!bindings.length) return [];
  await deleteEmptyPresentationBindingSelections({
    workspaceId: params.workspaceId,
    selectionKeys: bindings.map((entry) => entry.selectionKey),
    scopedRows: params.scopedRows,
    fixedRows: params.fixedRows,
    programProgressCells: params.programProgressCells,
  });
  return bindings;
}

export function emptyPresentationBindingPresenceKeys(
  entries: EmptyPresentationBindingEntry[]
): string[] {
  return entries.flatMap((entry) => [
    programContentSlidePresenceKey({ kind: 'presentation_ref', id: entry.presentationRefId }),
    programContentSlidePresenceKey({ kind: entry.sourceKind, id: entry.sourceId }),
  ]);
}

type PresentationRefBindingSource = {
  sourceKind: EmptyPresentationBindingSourceKind;
  sourceId: string;
  learningText: string | null | undefined;
};

function splitWorkspaceScope(scopeId: string): { scopeType: 'fixed' | 'track' | 'project' | ''; scopeKey: string } {
  const parts = scopeId.split('|');
  const scopeType = parts[0] === 'fixed' || parts[0] === 'track' || parts[0] === 'project' ? parts[0] : '';
  return { scopeType, scopeKey: parts.slice(5).join('|') };
}

async function findPresentationRefBindingSource(params: {
  workspaceId: string;
  presentationRefId: string;
  academicYear: number;
  termNumber: 1 | 2;
  scopeId?: string;
  annualCellKey?: string;
  contextGradeId?: string;
  createPresentationRefOnSave?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
}): Promise<PresentationRefBindingSource | null> {
  const sid = params.workspaceId.trim();
  const refId = params.presentationRefId.trim();
  if (!sid || !isPersistableUuid(refId)) return null;

  const ctx = params.createPresentationRefOnSave;
  const annualKey = (params.annualCellKey ?? ctx?.annualKey ?? '').trim();
  const gradeId =
    (params.contextGradeId ?? ctx?.gradeId ?? gradeIdFromAnnualCellKey(annualKey) ?? '').trim();
  if (!gradeId) return null;

  const matchScoped = (row: ProgramScopedContentRow): PresentationRefBindingSource | null => {
    const sourceId = (row.id ?? '').trim();
    if (!sourceId || !isPersistableUuid(sourceId)) return null;
    if (resolvePresentationRefIdFromLearningContent(row) !== refId) return null;
    return { sourceKind: 'scoped_row', sourceId, learningText: row.learning_text };
  };

  const matchFixed = (row: ProgramFixedContentRow): PresentationRefBindingSource | null => {
    const sourceId = (row.id ?? '').trim();
    if (!sourceId || !isPersistableUuid(sourceId)) return null;
    if (resolvePresentationRefIdFromLearningContent(row) !== refId) return null;
    return { sourceKind: 'fixed_row', sourceId, learningText: row.learning_text };
  };

  const matchCell = (cell: ProgramProgressCell): PresentationRefBindingSource | null => {
    const sourceId = (cell.id ?? '').trim();
    if (!sourceId || !isPersistableUuid(sourceId)) return null;
    if (resolvePresentationRefIdFromLearningContent(cell) !== refId) return null;
    return { sourceKind: 'program_cell', sourceId, learningText: cell.learning_text };
  };

  const scopeId = (params.scopeId ?? '').trim();
  const { scopeType, scopeKey } = splitWorkspaceScope(scopeId);

  if (ctx && Number.isFinite(ctx.orderIndex)) {
    if ((scopeType === 'track' || scopeType === 'project') && scopeKey) {
      const { data, error } = await supabase
        .from('program_scoped_content_rows')
        .select('*')
        .eq('workspace_id', sid)
        .eq('grade_id', gradeId)
        .eq('academic_year', params.academicYear)
        .eq('term_number', params.termNumber)
        .eq('scope_type', scopeType)
        .eq('scope_key', scopeKey)
        .eq('order_index', Number(ctx.orderIndex))
        .maybeSingle();
      if (error) throw new Error(error.message);
      const hit = data ? matchScoped(data as ProgramScopedContentRow) : null;
      if (hit) return hit;
    }
    if (scopeType === 'fixed') {
      const { data, error } = await supabase
        .from('program_fixed_content_rows')
        .select('*')
        .eq('workspace_id', sid)
        .eq('grade_id', gradeId)
        .eq('academic_year', params.academicYear)
        .eq('term_number', params.termNumber)
        .eq('order_index', Number(ctx.orderIndex))
        .maybeSingle();
      if (error) throw new Error(error.message);
      const hit = data ? matchFixed(data as ProgramFixedContentRow) : null;
      if (hit) return hit;
    }
    if (ctx.mondayIso && ctx.weekdayKey && Number.isFinite(ctx.slotIndex)) {
      const { data, error } = await supabase
        .from('program_progress_cells')
        .select('*')
        .eq('workspace_id', sid)
        .eq('grade_id', gradeId)
        .eq('academic_year', params.academicYear)
        .eq('term_number', params.termNumber)
        .eq('monday_iso', ctx.mondayIso)
        .eq('weekday_key', ctx.weekdayKey)
        .eq('slot_index', Number(ctx.slotIndex))
        .maybeSingle();
      if (error) throw new Error(error.message);
      const hit = data ? matchCell(data as ProgramProgressCell) : null;
      if (hit) return hit;
    }
  }

  if (annualKey) {
    const { data: scopedBySource, error: scopedBySourceError } = await supabase
      .from('program_scoped_content_rows')
      .select('*')
      .eq('workspace_id', sid)
      .eq('grade_id', gradeId)
      .eq('academic_year', params.academicYear)
      .eq('term_number', params.termNumber)
      .eq('source_annual_cell_key', annualKey);
    if (scopedBySourceError) throw new Error(scopedBySourceError.message);
    for (const row of scopedBySource ?? []) {
      const hit = matchScoped(row as ProgramScopedContentRow);
      if (hit) return hit;
    }

    const { data: fixedBySource, error: fixedBySourceError } = await supabase
      .from('program_fixed_content_rows')
      .select('*')
      .eq('workspace_id', sid)
      .eq('grade_id', gradeId)
      .eq('academic_year', params.academicYear)
      .eq('term_number', params.termNumber)
      .eq('source_annual_cell_key', annualKey);
    if (fixedBySourceError) throw new Error(fixedBySourceError.message);
    for (const row of fixedBySource ?? []) {
      const hit = matchFixed(row as ProgramFixedContentRow);
      if (hit) return hit;
    }
  }

  const parsedAnnual = annualKey ? parseAnnualCellKey(annualKey) : null;
  if (parsedAnnual) {
    const { data, error } = await supabase
      .from('program_progress_cells')
      .select('*')
      .eq('workspace_id', sid)
      .eq('grade_id', parsedAnnual.gradeId)
      .eq('academic_year', params.academicYear)
      .eq('term_number', params.termNumber)
      .eq('monday_iso', parsedAnnual.mondayIso)
      .eq('weekday_key', parsedAnnual.weekdayKey)
      .eq('slot_index', parsedAnnual.slotIndex)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const hit = data ? matchCell(data as ProgramProgressCell) : null;
    if (hit) return hit;
  }

  const { data: scopedRows, error: scopedError } = await supabase
    .from('program_scoped_content_rows')
    .select('*')
    .eq('workspace_id', sid)
    .eq('grade_id', gradeId)
    .eq('academic_year', params.academicYear)
    .eq('term_number', params.termNumber);
  if (scopedError) throw new Error(scopedError.message);
  for (const row of scopedRows ?? []) {
    const hit = matchScoped(row as ProgramScopedContentRow);
    if (hit) return hit;
  }

  const { data: fixedRows, error: fixedError } = await supabase
    .from('program_fixed_content_rows')
    .select('*')
    .eq('workspace_id', sid)
    .eq('grade_id', gradeId)
    .eq('academic_year', params.academicYear)
    .eq('term_number', params.termNumber);
  if (fixedError) throw new Error(fixedError.message);
  for (const row of fixedRows ?? []) {
    const hit = matchFixed(row as ProgramFixedContentRow);
    if (hit) return hit;
  }

  const { data: cells, error: cellsError } = await supabase
    .from('program_progress_cells')
    .select('*')
    .eq('workspace_id', sid)
    .eq('grade_id', gradeId)
    .eq('academic_year', params.academicYear)
    .eq('term_number', params.termNumber);
  if (cellsError) throw new Error(cellsError.message);
  for (const cell of cells ?? []) {
    const hit = matchCell(cell as ProgramProgressCell);
    if (hit) return hit;
  }

  return null;
}

/**
 * 연결 해제 대상 판정: 이 탭이 알던 ref와 DB 행의 현재 ref가 같을 때만 허용한다.
 * 탭이 ref를 몰랐거나(빈 화면) 다른 탭·기기가 새 ref를 연결했으면 그 연결·슬라이드를 지우지 않는다.
 */
function sourceRefMatchesExpected(
  expectedRef: string,
  hintText: string,
  row: Parameters<typeof resolvePresentationRefIdFromLearningContent>[0]
): boolean {
  if (!expectedRef) return false;
  const dbRef = resolvePresentationRefIdFromLearningContent(row);
  if (dbRef) return dbRef === expectedRef;
  const hintRef = resolvePresentationRefIdFromLearningContent(hintText);
  return !hintRef || hintRef === expectedRef;
}

async function resolveBindingFromSourceOwner(params: {
  workspaceId: string;
  sourceOwner: ProgramContentSlideOwnerRef;
  presentationRefId?: string;
  bindingLearningText?: string | null;
}): Promise<PresentationRefBindingSource | null> {
  const sid = params.workspaceId.trim();
  const sourceOwner = params.sourceOwner;
  const sourceId = sourceOwner.id?.trim() ?? '';
  if (!sid || !sourceId || !isPersistableUuid(sourceId)) return null;
  if (sourceOwner.kind === 'presentation_ref') return null;

  const expectedRef = (params.presentationRefId ?? '').trim();
  const hintText = (params.bindingLearningText ?? '').trim();

  if (sourceOwner.kind === 'scoped_row') {
    const row = await loadScopedRowById(sid, sourceId);
    if (!row) return null;
    const text = hintText || row.learning_text || '';
    if (!sourceRefMatchesExpected(expectedRef, text, row)) return null;
    return { sourceKind: 'scoped_row', sourceId, learningText: text || row.learning_text };
  }
  if (sourceOwner.kind === 'fixed_row') {
    const row = await loadFixedRowById(sid, sourceId);
    if (!row) return null;
    const text = hintText || row.learning_text || '';
    if (!sourceRefMatchesExpected(expectedRef, text, row)) return null;
    return { sourceKind: 'fixed_row', sourceId, learningText: text || row.learning_text };
  }
  const cell = await loadProgramCellById(sid, sourceId);
  if (!cell) return null;
  const text = hintText || cell.learning_text || '';
  if (!sourceRefMatchesExpected(expectedRef, text, cell)) return null;
  return { sourceKind: 'program_cell', sourceId, learningText: text || cell.learning_text };
}

export const PRESENTATION_REF_CONFLICT_MESSAGE =
  '다른 탭·기기에서 이 칸에 프레젠테이션이 이미 연결되었습니다. 덮어쓰지 않도록 저장을 멈췄습니다. 표를 새로고침한 뒤 다시 열어 주세요.';

/** 새 presentation_ref를 발급하기 전: 원본 행에 이미 (이 탭이 모르는) ref가 연결돼 있으면 그 ref를 돌려준다. */
export async function findExistingPresentationRefOnSourceOwner(params: {
  workspaceId: string;
  sourceOwner: ProgramContentSlideOwnerRef | null | undefined;
}): Promise<string> {
  const sid = params.workspaceId.trim();
  const sourceOwner = params.sourceOwner;
  const sourceId = sourceOwner?.id?.trim() ?? '';
  if (!sid || !sourceOwner || !sourceId || !isPersistableUuid(sourceId)) return '';
  if (sourceOwner.kind === 'presentation_ref') return '';
  const row =
    sourceOwner.kind === 'scoped_row'
      ? await loadScopedRowById(sid, sourceId)
      : sourceOwner.kind === 'fixed_row'
        ? await loadFixedRowById(sid, sourceId)
        : await loadProgramCellById(sid, sourceId);
  return row ? resolvePresentationRefIdFromLearningContent(row) : '';
}

/**
 * 기존 program_cell / scoped_row / fixed_row에 새로 발급한 presentation_ref_id를 "즉시" DB에 반영한다.
 * 플로팅 저장(슬라이드/질문) 경로에서 호출되어, 진도표 저장 없이도 칸↔프레젠 연결이 유지되게 한다.
 */
export async function persistPresentationRefIdToSourceOwner(params: {
  workspaceId: string;
  sourceOwner: ProgramContentSlideOwnerRef;
  presentationRefId: string;
}): Promise<void> {
  const sid = params.workspaceId.trim();
  const refId = params.presentationRefId.trim();
  const sourceOwner = params.sourceOwner;
  const sourceId = sourceOwner?.id?.trim() ?? '';
  if (!sid || !sourceId || !isPersistableUuid(sourceId) || !isPersistableUuid(refId)) return;
  if (sourceOwner.kind === 'presentation_ref') return;

  try {
    if (sourceOwner.kind === 'scoped_row') {
      const row = await loadScopedRowById(sid, sourceId);
      if (!row || resolvePresentationRefIdFromLearningContent(row) === refId) return;
      const fields = readProgramLearningContentFields(row);
      const written = writeProgramLearningContentFields({ ...fields, presentationRefId: refId });
      await upsertProgramScopedContentRows([{ ...row, ...written }]);
      return;
    }
    if (sourceOwner.kind === 'fixed_row') {
      const row = await loadFixedRowById(sid, sourceId);
      if (!row || resolvePresentationRefIdFromLearningContent(row) === refId) return;
      const fields = readProgramLearningContentFields(row);
      const written = writeProgramLearningContentFields({ ...fields, presentationRefId: refId });
      await upsertProgramFixedContentRows([{ ...row, ...written }]);
      return;
    }
    const cell = await loadProgramCellById(sid, sourceId);
    if (!cell || resolvePresentationRefIdFromLearningContent(cell) === refId) return;
    const fields = readProgramLearningContentFields(cell);
    const written = writeProgramLearningContentFields({ ...fields, presentationRefId: refId });
    await upsertProgramProgressCells([{ ...cell, ...written }]);
  } catch (e) {
    /**
     * 즉시 반영 실패 시에도 로컬 초안(learningPatch)·이후 진도표 저장으로 복구될 수 있으므로
     * 상위 저장(질문/슬라이드) 자체를 막지 않는다. 콘솔 로그로만 남긴다.
     */
    console.warn('[program-slide] presentation_ref_id 즉시 반영 실패', e);
  }
}

/** 슬라이드가 모두 비어 저장될 때 learning_text의 presentationRefId 연결을 제거한다. */
export async function clearPresentationRefBindingAfterEmptySlideSave(params: {
  workspaceId: string;
  presentationRefId?: string;
  sourceOwner?: ProgramContentSlideOwnerRef | null;
  bindingLearningText?: string | null;
  academicYear: number;
  termNumber: 1 | 2;
  scopeId?: string;
  annualCellKey?: string;
  contextGradeId?: string;
  createPresentationRefOnSave?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
}): Promise<{
  nextLearningText: string | null;
  sourceOwner: ProgramContentSlideOwnerRef;
} | null> {
  const sid = params.workspaceId.trim();
  if (!sid) return null;

  const refId =
    (params.presentationRefId ?? '').trim() ||
    resolvePresentationRefIdFromLearningContent(params.bindingLearningText ?? '') ||
    resolvePresentationRefIdFromLearningContent(params.createPresentationRefOnSave?.learningText ?? '');

  let binding: PresentationRefBindingSource | null = null;
  if (params.sourceOwner?.id?.trim() && params.sourceOwner.kind !== 'presentation_ref') {
    binding = await resolveBindingFromSourceOwner({
      workspaceId: sid,
      sourceOwner: params.sourceOwner,
      presentationRefId: refId || undefined,
      bindingLearningText: params.bindingLearningText,
    });
  }
  if (!binding && refId) {
    binding = await findPresentationRefBindingSource({
      workspaceId: sid,
      presentationRefId: refId,
      academicYear: params.academicYear,
      termNumber: params.termNumber,
      scopeId: params.scopeId,
      annualCellKey: params.annualCellKey,
      contextGradeId: params.contextGradeId,
      createPresentationRefOnSave: params.createPresentationRefOnSave,
    });
  }
  if (!binding) return null;

  const resolvedRefId = refId || resolvePresentationRefIdFromLearningContent(binding.learningText);
  if (resolvedRefId) {
    try {
      await deleteProgramContentSlidePagesForOwner(sid, { kind: 'presentation_ref', id: resolvedRefId });
    } catch {
      /* 슬라이드 페이지가 이미 비어 있어도 ref 해제는 계속 */
    }
  }

  const nextLearningText = learningTextStorageWithoutPresentationRef(binding.learningText);

  if (binding.sourceKind === 'scoped_row') {
    const row = await loadScopedRowById(sid, binding.sourceId);
    if (!row) return null;
    await persistLearningTextWithoutPresentationRef({
      workspaceId: sid,
      sourceKind: 'scoped_row',
      scopedRow: row,
      nextLearningText,
    });
  } else if (binding.sourceKind === 'fixed_row') {
    const row = await loadFixedRowById(sid, binding.sourceId);
    if (!row) return null;
    await persistLearningTextWithoutPresentationRef({
      workspaceId: sid,
      sourceKind: 'fixed_row',
      fixedRow: row,
      nextLearningText,
    });
  } else {
    const cell = await loadProgramCellById(sid, binding.sourceId);
    if (!cell) return null;
    await persistLearningTextWithoutPresentationRef({
      workspaceId: sid,
      sourceKind: 'program_cell',
      programCell: cell,
      nextLearningText,
    });
  }

  invalidateProgramContentSlidePresenceCache(sid);
  return {
    nextLearningText,
    sourceOwner: { kind: binding.sourceKind, id: binding.sourceId },
  };
}
